// Small seeded PRNG (mulberry32) so a simulated league is reproducible.

export interface Rng {
  next(): number;
  int(min: number, maxInclusive: number): number;
  pick<T>(items: readonly T[]): T;
  chance(p: number): boolean;
  /** Roughly normal around mean with the given spread, clamped to [min, max]. */
  around(mean: number, spread: number, min?: number, max?: number): number;
  shuffle<T>(items: readonly T[]): T[];
}

export function hashSeed(...parts: (string | number)[]): number {
  let h = 2166136261;
  for (const part of parts) {
    const s = String(part);
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    h ^= 0x9e3779b9;
  }
  return h >>> 0;
}

/**
 * The two draws a taunt needs: whether to speak, and what to say.
 *
 * WHY THIS IS ONE FUNCTION AND NOT TWO CALLS AT EACH SITE. Both call sites
 * used to build one number and hand it in as both, which is the whole of
 * AUDIT-2026-09-19 F17 — the gate passes only below the dial, so the same
 * number could only ever index the front of the pool. Putting the pair here
 * makes it the one place a future call site could re-tangle them, which is
 * the one place a test can hold them.
 *
 * THE GATE'S KEY IS DELIBERATELY UNCHANGED. `random` is still
 * `hashSeed(seed, key)`, so which seats speak and how often is byte-identical
 * to before the fix; only the sentence moves. The second draw hangs off a
 * derived key, and every `rng()` here builds its own generator and takes one
 * `next()`, so adding it advances no shared sequence and perturbs nothing
 * else in a seeded run.
 */
export function talkDraws(seed: string | number, key: string): { random: number; line: number } {
  return {
    random: rng(hashSeed(seed, key)).next(),
    line: rng(hashSeed(seed, `${key}:line`)).next(),
  };
}

export function rng(seed: number): Rng {
  let a = seed >>> 0;
  const next = () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  return {
    next,
    int: (min, max) => min + Math.floor(next() * (max - min + 1)),
    pick: (items) => items[Math.floor(next() * items.length)]!,
    chance: (p) => next() < p,
    around: (mean, spread, min = -Infinity, max = Infinity) => {
      const n = (next() + next() + next() - 1.5) * 2; // ~N(0,1)-ish
      return Math.min(max, Math.max(min, mean + n * spread));
    },
    shuffle: (items) => {
      const out = [...items];
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(next() * (i + 1));
        [out[i], out[j]] = [out[j]!, out[i]!];
      }
      return out;
    },
  };
}
